// ═══════════════════════════════════════════════════════════════════════════════
// Create / edit budget period (employer only)
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { IsoDatePicker } from '../../components/IsoDatePicker';
import { AppButton } from '../../components/ui/AppButton';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { useToast } from '../../components/ui/ToastProvider';
import { useLanguage } from '../../contexts/LanguageContext';
import { resolveApiErrorMessage } from '../../i18n/mapApiClientError';
import { budgetApi } from '../../lib/api';

const parseAmount = (raw: string): number | null => {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
};

export const BudgetFormPage = () => {
  const { budgetId } = useParams<{ budgetId: string }>();
  const { t } = useLanguage();
  const { showSuccessToast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const isCreate = !budgetId;
  const parentBudgetId = isCreate ? (searchParams.get('parentBudgetId') ?? '') : '';
  const isAddOnCreate = isCreate && parentBudgetId !== '';

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [amountInput, setAmountInput] = useState('');

  const [loading, setLoading] = useState(!isCreate);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLocked, setIsLocked] = useState(false);
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removeDialogError, setRemoveDialogError] = useState<string | null>(null);
  const [parentRange, setParentRange] = useState<{ startDate: string; endDate: string } | null>(null);
  const [initialFormSnapshot, setInitialFormSnapshot] = useState<{
    startDate: string;
    endDate: string;
    amountInput: string;
  } | null>(null);

  const loadBudget = useCallback(async () => {
    if (!budgetId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await budgetApi.get(budgetId);
      if (res.success && res.data) {
        const { budget, isLocked: locked } = res.data;
        setStartDate(budget.startDate);
        setEndDate(budget.endDate);
        setAmountInput(String(budget.amount));
        setInitialFormSnapshot({
          startDate: budget.startDate,
          endDate: budget.endDate,
          amountInput: String(budget.amount),
        });
        setIsLocked(locked);
        return;
      }
      setError(res.error ?? t('budget.loadError'));
    } catch {
      setError(t('budget.loadError'));
    } finally {
      setLoading(false);
    }
  }, [budgetId, t]);

  useEffect(() => {
    if (isCreate) return;
    void loadBudget();
  }, [isCreate, loadBudget]);

  useEffect(() => {
    if (!isAddOnCreate || !parentBudgetId) {
      return;
    }
    const loadParentBudget = async () => {
      const res = await budgetApi.get(parentBudgetId);
      if (!res.success || !res.data) {
        setError(res.error ?? t('budget.loadError'));
        return;
      }
      const parent = res.data.budget;
      setParentRange({ startDate: parent.startDate, endDate: parent.endDate });
      if (startDate === '') {
        setStartDate(parent.startDate);
      }
      if (endDate === '') {
        setEndDate(parent.endDate);
      }
    };
    void loadParentBudget();
  }, [endDate, isAddOnCreate, parentBudgetId, startDate, t]);

  const validateForm = (): { startDate: string; endDate: string; amount: number } | null => {
    if (!startDate || !endDate) {
      setError(t('budget.validation.rangeRequired'));
      return null;
    }
    if (startDate > endDate) {
      setError(t('budget.validation.dates'));
      return null;
    }
    if (isAddOnCreate && parentRange) {
      if (startDate < parentRange.startDate || endDate > parentRange.endDate) {
        setError(t('apiError.subBudgetOutOfMainRange'));
        return null;
      }
    }
    const amount = parseAmount(amountInput);
    if (amount === null) {
      setError(t('budget.validation.amount'));
      return null;
    }
    return { startDate, endDate, amount };
  };

  const hasEditFormChanges = (): boolean => {
    if (isCreate || initialFormSnapshot === null) {
      return true;
    }
    return (
      startDate !== initialFormSnapshot.startDate ||
      endDate !== initialFormSnapshot.endDate ||
      amountInput.trim() !== initialFormSnapshot.amountInput.trim()
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const payload = validateForm();
    if (!payload) return;
    if (!isCreate && !hasEditFormChanges()) {
      showSuccessToast(t('toast.budget.updateSuccess'));
      navigate('/settlements');
      return;
    }

    setSubmitting(true);
    try {
      if (isCreate) {
        const res = await budgetApi.create(
          isAddOnCreate
            ? { ...payload, budgetKind: 'sub', parentBudgetId }
            : payload
        );
        if (res.success) {
          showSuccessToast(t('toast.budget.createSuccess'));
          navigate('/settlements');
          return;
        }
        setError(res.error ?? t('budget.saveError'));
      } else if (budgetId) {
        const res = await budgetApi.update(budgetId, payload);
        if (res.success) {
          showSuccessToast(t('toast.budget.updateSuccess'));
          navigate('/settlements');
          return;
        }
        setError(res.error ?? t('budget.saveError'));
      }
    } catch (err) {
      setError(resolveApiErrorMessage(err, t, 'budget.saveError'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const buildRemoveDescription = (): string => {
    if (!removeDialogError) {
      return t('budget.removeConfirmBody');
    }
    return `${t('budget.removeConfirmBody')}\n\n${removeDialogError}`;
  };

  const handleOpenRemoveConfirm = () => {
    setRemoveDialogError(null);
    setRemoveConfirmOpen(true);
  };

  const handleCancelRemoveConfirm = () => {
    setRemoveConfirmOpen(false);
    setRemoveDialogError(null);
  };

  const handleConfirmRemoveBudget = async () => {
    if (!budgetId || removing) return;
    setRemoving(true);
    setRemoveDialogError(null);
    try {
      const res = await budgetApi.remove(budgetId);
      if (res.success) {
        setRemoveConfirmOpen(false);
        showSuccessToast(t('toast.budget.deleteSuccess'));
        navigate('/settlements');
        return;
      }
      setRemoveDialogError(res.error ?? t('budget.removeError'));
    } catch (err) {
      setRemoveDialogError(resolveApiErrorMessage(err, t, 'budget.removeError'));
    } finally {
      setRemoving(false);
    }
  };

  const pageTitle = isCreate
    ? isAddOnCreate
      ? t('budget.form.addOnCreateTitle')
      : t('budget.form.createTitle')
    : t('budget.form.editTitle');
  const showRemoveButton = !isCreate && !isLocked;

  return (
    <div className="min-h-full bg-gray-50 dark:bg-gray-950">
      <header className="z-10 bg-white shadow-sm dark:bg-gray-900 dark:shadow-black/20">
        <div className="mx-auto max-w-lg px-4 py-4">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleBack}
              aria-label={t('common.back')}
              className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-200 dark:hover:bg-gray-800"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">{pageTitle}</h1>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-lg px-4 py-6 pb-24">
        {loading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="space-y-4 rounded-xl bg-white p-4 shadow-sm dark:bg-gray-900 dark:shadow-black/20"
          >
            {!isCreate && isLocked ? (
              <p
                className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-100"
                role="status"
              >
                {t('budget.lockedEditHint')}
              </p>
            ) : null}

            {isAddOnCreate && parentRange ? (
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {t('budget.form.parentPeriod')}: {parentRange.startDate} - {parentRange.endDate}
              </p>
            ) : null}

            <IsoDatePicker
              id="budget-start"
              label={t('budget.form.startDate')}
              value={startDate}
              onChange={setStartDate}
              minDate={isAddOnCreate ? (parentRange?.startDate ?? undefined) : undefined}
              maxDate={
                isAddOnCreate
                  ? (endDate || parentRange?.endDate || undefined)
                  : (endDate || undefined)
              }
            />

            <IsoDatePicker
              id="budget-end"
              label={t('budget.form.endDate')}
              value={endDate}
              onChange={setEndDate}
              minDate={
                isAddOnCreate
                  ? (startDate || parentRange?.startDate || undefined)
                  : (startDate || undefined)
              }
              maxDate={isAddOnCreate ? (parentRange?.endDate ?? undefined) : undefined}
            />

            <div>
              <label
                className="block text-sm font-medium text-gray-700 dark:text-gray-200"
                htmlFor="budget-amount"
              >
                {t('budget.form.amount')}
              </label>
              <input
                id="budget-amount"
                type="number"
                inputMode="decimal"
                min={0}
                step="0.01"
                value={amountInput}
                onChange={(e) => setAmountInput(e.target.value)}
                required
                className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                aria-describedby="budget-amount-hint"
              />
              <p id="budget-amount-hint" className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {t('budget.form.amountHint')}
              </p>
            </div>

            {error ? (
              <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                {error}
              </p>
            ) : null}

            <div className="flex flex-wrap gap-2 pt-2">
              <AppButton
                type="submit"
                variant="primary"
                disabled={submitting || removing}
                className="flex-1 shadow-sm"
                aria-label={isCreate ? t('budget.form.submitCreate') : t('budget.form.submitEdit')}
              >
                {submitting ? t('budget.form.saving') : isCreate ? t('budget.form.submitCreate') : t('budget.form.submitEdit')}
              </AppButton>
              <AppButton type="button" variant="secondary" onClick={handleBack} disabled={removing}>
                {t('budget.form.cancel')}
              </AppButton>
            </div>

            {showRemoveButton ? (
              <div className="mt-4 border-t border-gray-200 pt-4 dark:border-gray-700">
                <button
                  type="button"
                  onClick={handleOpenRemoveConfirm}
                  disabled={submitting || removing}
                  className="min-h-11 w-full rounded-lg border border-red-300 bg-white px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 dark:border-red-800 dark:bg-gray-900 dark:text-red-300 dark:hover:bg-red-950/40"
                  aria-label={t('budget.form.removeAria')}
                >
                  {t('budget.form.remove')}
                </button>
              </div>
            ) : null}
          </form>
        )}
      </main>

      <ConfirmDialog
        open={removeConfirmOpen}
        title={t('budget.removeConfirmTitle')}
        description={buildRemoveDescription()}
        confirmLabel={removing ? t('budget.form.removing') : t('budget.form.removeConfirm')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => {
          void handleConfirmRemoveBudget();
        }}
        onCancel={handleCancelRemoveConfirm}
        variant="danger"
        confirmDisabled={removing}
      />
    </div>
  );
};
